import { inject, Injectable } from '@angular/core';
import { IMPORT_NOTIFICATION_HUB_CONSTANTS } from '../shared/constants/import-notification-hub-constants';
import { NotificationBaseService } from './notification-base-service';
import { ImportCompleted } from '../shared/models/notifications/import-completed';
import { ServiceApiService } from '../app/services/api/service-api.service';
import { firstValueFrom } from 'rxjs';
import { CommonUtils } from '../app/common/common-utils';
import { HotToastService } from '@ngxpert/hot-toast';

@Injectable({
  providedIn: 'root',
})
export class ImportCompletedNotificationsService extends NotificationBaseService {
  private importCompletedNotifications: ImportCompleted[] = [];
  private readonly importCompletionNotificationsKey = 'receivedImportCompletedNotifications';

  private readonly serviceApi = inject(ServiceApiService);
  private readonly toast = inject(HotToastService);

  constructor() {
    super(IMPORT_NOTIFICATION_HUB_CONSTANTS.ROUTE);
    this.importCompletedNotifications = this.getFromLocalStorage<ImportCompleted>(
      this.importCompletionNotificationsKey
    );
    this.receivedNotificationSignal.set(this.importCompletedNotifications);
  }

  public removeImportCompletedNotification(notificationId: string): void {
    this.importCompletedNotifications = this.importCompletedNotifications.filter(
      (notification) => notification.notificationId !== notificationId
    );
    this.receivedNotificationSignal.set(this.importCompletedNotifications);
    this.removeFromLocalStorage(this.importCompletionNotificationsKey, notificationId);
  }

  protected subscribeToNotifications(): void {
    this.hubConnection?.on(IMPORT_NOTIFICATION_HUB_CONSTANTS.HUB_METHOD, (importCompleted: ImportCompleted) => {
      if (this.currentUser && this.currentUser === importCompleted.user) {
        this.handleImportFailure(importCompleted);
        importCompleted.notificationId = crypto.randomUUID();
        this.importCompletedNotifications.push(importCompleted);
        this.receivedNotificationSignal.set(this.importCompletedNotifications);
        this.addToLocalStorage(this.importCompletionNotificationsKey, importCompleted);
        this.performReportDownload(importCompleted.importId ?? '', importCompleted.displayId ?? 'import-report');
      }
    });
  }

  private handleImportFailure(importCompleted: ImportCompleted): void {
    if (!importCompleted.isImportSuccessful) {
      importCompleted.error = true;
      this.toast.error('Errors occurred during import. Please check the log for details.', {
        autoClose: false,
        dismissible: true,
      });
    }
  }

  private async performReportDownload(importId: string, displayId: string): Promise<void> {
    const blob = await firstValueFrom(this.serviceApi.getImportLogfile(importId));
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

    CommonUtils.downloadFile(blob, `${displayId}-import-log-${timestamp}.txt`);
  }
}
